import type { Brand } from '../../utils/tokenManager';
import {
  DEFAULT_FALLBACK_PRIORITY,
  NodeSk6ConfigSchema,
  NodeProtocolSchema,
  FINGERPRINT_PROTOCOL_KEYS,
  type FallbackPriorityValue,
  type FingerprintMap,
  type FingerprintProtocolKey,
  type NodeProtocol,
} from '../../schemas/node';

export type NodeType = 'sk6' | 'vless';

export type NodeCategory = 'primary' | 'backup' | 'overseas' | 'internal';
/** BO-owned member close-plane readiness.  `ready` is never a UI write value. */
export type MemberRevocationStatus = 'disabled' | 'verifying' | 'ready';

/** Safe projection returned by the node-list API; it intentionally has no IDs or diagnostics. */
export interface MemberRevocationVerification {
  state: 'pending' | 'succeeded' | 'failed' | 'cancelled';
  failureCode: string | null;
  startedAt: string;
  expiresAt: string;
  completedAt: string | null;
}
export interface MemberRevocationHealth {
  status: 'legacy' | 'healthy' | 'degraded' | 'stale';
  lastReportedAt: string | null;
  leaseExpiresAt: string | null;
  failureCode: string | null;
}

export type MemberRevocationUiAction = 'verify' | 'disable';

/**
 * Member revocation is enforced by the SK6 node service. VLESS nodes (for
 * example the overseas-acceleration relays) do not run that service or its
 * revoke agent, so the BO must not offer verification controls for them.
 * Missing nodeType remains SK6 for compatibility with legacy list rows.
 */
export function supportsMemberRevocation(node: {
  nodeType?: NodeType;
}): boolean {
  return (node.nodeType ?? 'sk6') === 'sk6';
}

export interface MemberRevocationUiState {
  label:
    | 'Disabled'
    | 'Verifying'
    | 'Ready'
    | 'Stale'
    | 'Needs check'
    | 'Pending verification';
  failureLabel: string | null;
  action: MemberRevocationUiAction;
  actionLabel: 'Verify and enable' | 'Disable';
  actionDisabled: boolean;
}

/**
 * Verification state never disables cancellation.  The caller supplies only
 * row-local request state, so a request for node A cannot block node B.
 */
export function isMemberRevocationActionDisabled(
  sharedInfraReadOnly: boolean,
  actionInFlight: boolean
): boolean {
  return sharedInfraReadOnly || actionInFlight;
}

/**
 * UI-owned translations of the finite safe server codes.  Deliberately never
 * render an unknown value from the API: it might be a future diagnostic that
 * is not intended for an operator-facing node list.
 */
const MEMBER_REVOCATION_FAILURE_LABELS: Record<string, string> = {
  agent_timeout: 'Node agent timed out',
  socket_unavailable: 'Revocation socket unavailable',
  socket_access_denied: 'Revocation socket access denied',
  socket_permissions_invalid: 'Revocation socket permissions invalid',
  control_config_missing: 'SK6 control configuration missing',
  verification_internal_error: 'Node verification internal error',
  health_internal_error: 'Node health check internal error',
  lease_expired: 'Health lease expired; re-verify node',
};

export function getMemberRevocationUiState(
  status: MemberRevocationStatus | undefined,
  verification?: MemberRevocationVerification,
  health?: MemberRevocationHealth
): MemberRevocationUiState {
  const normalizedStatus = status ?? 'disabled';
  const isPending =
    normalizedStatus === 'verifying' || verification?.state === 'pending';
  if (isPending) {
    return {
      label: 'Verifying',
      failureLabel: null,
      action: 'disable',
      actionLabel: 'Disable',
      // A pending verification must remain cancellable.  The row action is
      // disabled only by its own in-flight request in NodeList.
      actionDisabled: false,
    };
  }
  if (normalizedStatus === 'ready' && health?.status === 'stale') {
    return {
      label: 'Stale',
      failureLabel:
        MEMBER_REVOCATION_FAILURE_LABELS[health.failureCode ?? ''] ??
        'Health lease expired; re-verify node',
      action: 'verify', actionLabel: 'Verify and enable', actionDisabled: false,
    };
  }
  if (normalizedStatus === 'ready' && health?.status === 'legacy') {
    return {
      label: 'Pending verification',
      failureLabel: null,
      action: 'disable',
      actionLabel: 'Disable',
      actionDisabled: false,
    };
  }
  if (normalizedStatus === 'ready') {
    return {
      label: 'Ready',
      failureLabel:
        health?.status === 'degraded'
          ? MEMBER_REVOCATION_FAILURE_LABELS[health.failureCode ?? ''] ??
            'Node health check failed; monitor node'
          : null,
      action: 'disable',
      actionLabel: 'Disable',
      actionDisabled: false,
    };
  }
  if (verification?.state === 'failed') {
    return {
      label: 'Needs check',
      failureLabel:
        MEMBER_REVOCATION_FAILURE_LABELS[verification.failureCode ?? ''] ??
        'Verification failed; check node',
      action: 'verify',
      actionLabel: 'Verify and enable',
      actionDisabled: false,
    };
  }
  return {
    label: 'Disabled',
    failureLabel: null,
    action: 'verify',
    actionLabel: 'Verify and enable',
    actionDisabled: false,
  };
}

/**
 * The UI has exactly two member-revocation writes.  Keeping request assembly
 * here makes it impossible for a future button to accidentally PATCH ready.
 */
export function memberRevocationActionRequest(
  id: number,
  action: MemberRevocationUiAction
): { url: string; init: RequestInit } {
  if (action === 'verify') {
    return {
      url: `/api/admin/nodes/${id}/member-revocation/verify`,
      init: { method: 'POST' },
    };
  }
  return {
    url: `/api/admin/nodes/${id}/member-revocation`,
    init: { method: 'PATCH', body: JSON.stringify({ status: 'disabled' }) },
  };
}

/** The list is the source of truth for whether the three-second poll runs. */
export function shouldPollMemberRevocationNodes(
  nodes: ReadonlyArray<{
    nodeType?: NodeType;
    memberRevocationStatus?: MemberRevocationStatus;
  }>
): boolean {
  return nodes.some(
    node =>
      supportsMemberRevocation(node) &&
      node.memberRevocationStatus === 'verifying'
  );
}

/** Render server timestamps only after validating them as dates. */
export function memberRevocationLastCheckedLabel(
  verification?: MemberRevocationVerification
): string | null {
  const rawTimestamp = verification?.completedAt ?? verification?.startedAt;
  if (!rawTimestamp) return null;
  const timestamp = new Date(rawTimestamp);
  if (Number.isNaN(timestamp.getTime())) return null;
  return `Last checked: ${timestamp.toLocaleString()}`;
}

export function memberRevocationHealthLabel(
  health?: MemberRevocationHealth
): string | null {
  // Legacy is a migration rollout state, never a successful health report.
  // Do not let malformed or future data make it look verified.
  if (health?.status === 'legacy') return null;
  if (!health?.lastReportedAt || Number.isNaN(new Date(health.lastReportedAt).getTime())) return null;
  return `Health: ${health.status} · ${new Date(health.lastReportedAt).toLocaleString()}`;
}

/**
 * 2026-07-02 — 節點營運標記上限（鏡像後端 node.dto.ts NODE_TAG_MAX_COUNT /
 * NODE_TAG_MAX_LENGTH；改動要兩邊同步）。
 */
export const MAX_NODE_TAGS = 16;
export const MAX_NODE_TAG_LENGTH = 24;

/**
 * 2026-07-02 — 新增節點品牌可見性「明確表態」判定（鏡像後端
 * `vpn-manage-server/src/utils/node_brand_scope.ts` assertNodeBrandScopeExplicit
 * 語意；改動要兩邊同步）。
 *
 * 放行（true）：
 *   - allowedBrands 非空陣列（限定品牌）
 *   - shareAllBrands === true（明確全品牌共享）
 * 擋下（false）：
 *   - allowedBrands undefined / null / 空陣列，且 shareAllBrands !== true
 *
 * 前端在「新增節點」submit 前用此函數擋下空品牌 payload（撞後端 400 的
 * 根除點）；編輯模式不套（後端 guard 只攔 CREATE，29 個既有 NULL-brand
 * 節點編輯不受影響）。
 */
export function isNodeBrandScopeExplicit(input: {
  allowedBrands?: unknown;
  shareAllBrands?: unknown;
}): boolean {
  const hasExplicitBrands =
    Array.isArray(input.allowedBrands) && input.allowedBrands.length > 0;
  return hasExplicitBrands || input.shareAllBrands === true;
}

export interface NodeRecord {
  id: number;
  ip: string;
  port: number;
  /** 內部節點名稱（operator 用，唯一 key） */
  name: string;
  /**
   * 用戶端顯示名稱（dashboard 看到的中文友善名）。Migration 1711756800059 加入；
   * 既有節點 backfill 自 region。Optional/nullable 以兼容舊資料。
   */
  displayName?: string | null;
  /** 地區（hk/au/sz/overseas/overseas_accel 或自由格式） */
  region: string;
  config: string;
  nodeType?: NodeType;
  nodeCategory?: NodeCategory;
  /**
   * 2026-07-02 — 營運自訂標記（BO 內部用，例 ["限速","測試","廠商:IDCS"]）。
   * NULL/缺 = 無標記。不下發 client（後端 toPublicNodeDto 隔離）。
   */
  tags?: string[] | null;
  /** AR-Q7 蜜罐節點：true 時客戶端 manifest 不下發，僅供逆向誘捕。 */
  isDecoy?: boolean;
  allowedBrands?: Brand[] | null;
  backupDomains?: string[] | null;
  minTier?: number;
  monitoringEnabled: boolean;
  monitoringPort: number | null;
  isEnabled: boolean;
  disabledReason: string | null;
  /** BO-controlled close-plane readiness; absent legacy rows are disabled. */
  memberRevocationStatus?: MemberRevocationStatus;
  /** Latest safe verification result; list projection has no attempt IDs/diagnostics. */
  memberRevocationVerification?: MemberRevocationVerification;
  memberRevocationHealth?: MemberRevocationHealth;
  /**
   * Jenkins 多節點部署參數（migration 067 / `vpn_ssh_credential_id` 等三欄）。
   * 非 AWS HK/AU 預設節點（自架 / CN-VPS / reality cluster）才需要設；
   * `null` = Jenkinsfile fallback 到 legacy AWS 預設。list API 已回傳這三欄，
   * 編輯面板讀回顯示，提交時連同基本欄位一起 PUT 回去。
   */
  sshCredentialId?: string | null;
  deployPath?: string | null;
  serviceName?: string | null;
  healthStatus?: string;
  lastLatencyMs?: number | null;
  createdAt: string;
}

export interface NodeFormValues {
  /** 內部節點名稱（operator 用） */
  name: string;
  /** 用戶端顯示名稱 */
  displayName?: string;
  ip: string;
  port: number;
  region: string;
  config?: string;
  nodeType?: NodeType;
  nodeCategory?: NodeCategory;
  /** 營運自訂標記（Select mode="tags" 綁定；送出時 trim + 去空）。 */
  tags?: string[];
  /** AR-Q7 蜜罐節點：true 時客戶端 manifest 不下發，僅供逆向誘捕。 */
  isDecoy?: boolean;
  allowedBrands?: Brand[];
  /**
   * 2026-06-11 fail-closed — 明確 opt-in「對所有品牌共享」。新增節點時若沒選
   * 可見品牌，必須勾此才放行（後端 assertNodeBrandScopeExplicit guard）。
   */
  shareAllBrands?: boolean;
  backupDomainsText?: string;
  minTier?: number;
  isEnabled?: boolean;
  /** 節點監控（sing-box Clash API external-controller）開關。 */
  monitoringEnabled?: boolean;
  /** 節點監控端口（Clash API external-controller port，預設 9090）。 */
  monitoringPort?: number;
  /**
   * 伺服器部署參數（Jenkins 多節點 deploy；對應 entity
   * `sshCredentialId` / `deployPath` / `serviceName`）。空字串/留空 = 送 null
   * 讓後端清回 legacy AWS 預設。表單有獨立輸入框，必須一併送 payload，
   * 否則 operator 改了卻 silently 不更新。
   */
  sshCredentialId?: string;
  deployPath?: string;
  serviceName?: string;
  expectedExitIpsText?: string;
  urltestUrl?: string;
  vlessUuid?: string;
  vlessWsHost?: string;
  vlessWsPath?: string;
  vlessSni?: string;
  /** SK6 節點動態 fallback 協議陣列（只用於 nodeType='sk6'） */
  protocols?: NodeProtocol[];
  relayOnly?: boolean;
  originHidden?: boolean;
  cdnRelayEnabled?: boolean;
  cdnDomain?: string;
  cdnPort?: number;
  sk6WsPort?: number;
  sk6WsPath?: string;
  anytlsEnabled?: boolean;
  anytlsDomain?: string;
  anytlsPort?: number;
  anytlsPassword?: string;
  anytlsSni?: string;
  fallbackPriority?: FallbackPriorityValue[];
  /** AB-D6 — per-protocol uTLS fingerprint override pushed to clients. */
  fingerprintMap?: FingerprintMap;
  dnsTxtDiscoveryEnabled?: boolean;
  dnsTxtNamesText?: string;
  dnsTxtDohEndpointsText?: string;
  dnsTxtMaxAgeSec?: number;
  dnsTxtExpectedKid?: string;
  connectionRotationEnabled?: boolean;
  connectionRotationMinMinutes?: number;
  connectionRotationMaxMinutes?: number;
  /**
   * Sprint 19 Wave 6 (W6-A) — DAITA (Defense Against AI Traffic Analysis).
   * Authoring-only Wave 6; the libbox stamper is wired on Android (Np.kt)
   * and iOS (box.swift applyDaitaIos); macOS / Windows ride future waves.
   * Sub-fields are flattened onto NodeFormValues so AntD `Form.Item name`
   * can bind directly. Persisted to `nodeConfigJson.daita.{...}`.
   */
  daitaEnabled?: boolean;
  daitaMachineSpec?: string;
  daitaReplyMachineSpec?: string;
  daitaMaxPaddingFrac?: number;
  daitaMaxBlockingFrac?: number;
}

/**
 * Sprint 19 Wave 6 (W6-A) — DAITA constants for clamping + defaults.
 * Mirrors the server-side `normalizeDaita` contract in
 * `vpn-manage-server/src/service/ip.service.ts` so client + server agree
 * on the field set + clamping behaviour. Keep the literals here so the
 * frontend doesn't need a separate `dto`-style import.
 */
export const DAITA_DEFAULT_MAX_PADDING_FRAC = 0.5;
export const DAITA_DEFAULT_MAX_BLOCKING_FRAC = 0.1;
export const DAITA_MAX_SPEC_LEN = 16384;

function clampFrac(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * 從任意 config 結構安全解析 protocols 陣列，失敗則回空陣列。
 * 允許部分 row 格式不合（舊資料）— 只保留通過 schema 的項目。
 */
export function parseProtocolsFromConfig(
  config: Record<string, unknown>
): NodeProtocol[] {
  const raw = config.protocols;
  if (!Array.isArray(raw)) return [];
  const out: NodeProtocol[] = [];
  for (const item of raw) {
    const parsed = NodeProtocolSchema.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

export function parseBackupDomains(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }
  if (typeof value !== 'string') return [];
  return value
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean);
}

export function parseTextList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }
  if (typeof value !== 'string') return [];
  return value
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean);
}

export function parseConfigObject(value?: string): Record<string, unknown> {
  if (!value?.trim()) return {};
  const parsed = JSON.parse(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('config must be a JSON object');
  }
  return parsed as Record<string, unknown>;
}

export function formatConfig(value?: string): string {
  const parsed = parseConfigObject(value);
  return Object.keys(parsed).length > 0 ? JSON.stringify(parsed, null, 2) : '';
}

export function nodeToFormValues(node: NodeRecord): Partial<NodeFormValues> {
  const nodeType = node.nodeType || 'sk6';
  const values: Partial<NodeFormValues> = {
    ip: node.ip,
    port: node.port,
    name: node.name,
    displayName: node.displayName ?? '',
    region: node.region,
    config: formatConfig(node.config),
    nodeType,
    nodeCategory: node.nodeCategory || 'primary',
    tags: node.tags || [],
    isDecoy: node.isDecoy === true,
    allowedBrands: node.allowedBrands || [],
    backupDomainsText: parseBackupDomains(node.backupDomains).join('\n'),
    minTier: node.minTier ?? 0,
    isEnabled: node.isEnabled !== false,
    // 監控欄位回填（enabled/port）。secret 不在此表單管理（後端 allowedKeys
    // 安全契約刻意不開放從節點表單寫入，同 nodeSecret）。
    monitoringEnabled: node.monitoringEnabled === true,
    monitoringPort: node.monitoringPort ?? 9090,
    // 伺服器部署參數回填（兩種 nodeType 都適用）。null → 空字串讓輸入框可清空。
    sshCredentialId: node.sshCredentialId ?? '',
    deployPath: node.deployPath ?? '',
    serviceName: node.serviceName ?? '',
    protocols: [],
  };

  if (nodeType === 'sk6') {
    try {
      const config = parseConfigObject(node.config);
      const parsed = NodeSk6ConfigSchema.safeParse(config);
      const sk6Config = parsed.success ? parsed.data : config;
      const cdnRelay = (
        sk6Config.cdnRelay && typeof sk6Config.cdnRelay === 'object'
          ? sk6Config.cdnRelay
          : undefined
      ) as Record<string, unknown> | undefined;
      const anytls = (
        sk6Config.anytls && typeof sk6Config.anytls === 'object'
          ? sk6Config.anytls
          : undefined
      ) as Record<string, unknown> | undefined;
      const dnsTxtDiscovery = (
        sk6Config.dnsTxtDiscovery &&
        typeof sk6Config.dnsTxtDiscovery === 'object'
          ? sk6Config.dnsTxtDiscovery
          : undefined
      ) as Record<string, unknown> | undefined;
      const connectionRotation = (
        sk6Config.connectionRotation && typeof sk6Config.connectionRotation === 'object'
          ? sk6Config.connectionRotation
          : sk6Config.rotation && typeof sk6Config.rotation === 'object'
            ? sk6Config.rotation
            : undefined
      ) as Record<string, unknown> | undefined;
      values.protocols = parseProtocolsFromConfig(config);
      values.expectedExitIpsText = parseTextList(
        sk6Config.expectedExitIps ?? sk6Config.egressIps
      ).join('\n');
      values.urltestUrl = String(sk6Config.urltestUrl || '');
      values.relayOnly = sk6Config.relayOnly === true;
      values.originHidden = sk6Config.originHidden === true;
      values.cdnRelayEnabled =
        cdnRelay?.enabled === true || Boolean(sk6Config.cdnDomain);
      values.cdnDomain = String(cdnRelay?.domain || sk6Config.cdnDomain || '');
      values.cdnPort = Number(cdnRelay?.port || sk6Config.cdnPort || 443);
      values.sk6WsPort = Number(
        cdnRelay?.wsPort || sk6Config.sk6WsPort || sk6Config.wsPort || 80
      );
      values.sk6WsPath = String(
        cdnRelay?.wsPath || sk6Config.sk6WsPath || sk6Config.wsPath || '/'
      );
      values.anytlsEnabled = anytls?.enabled === true;
      values.anytlsDomain = String(anytls?.domain || '');
      values.anytlsPort = Number(anytls?.port || 443);
      values.anytlsPassword = String(anytls?.password || '');
      values.anytlsSni = String(anytls?.sni || '');
      values.fallbackPriority = Array.isArray(sk6Config.fallbackPriority)
        ? (sk6Config.fallbackPriority as FallbackPriorityValue[])
        : [...DEFAULT_FALLBACK_PRIORITY];
      values.fingerprintMap = parseFingerprintMap(sk6Config.fingerprintMap);
      values.dnsTxtDiscoveryEnabled = dnsTxtDiscovery?.enabled === true;
      values.dnsTxtNamesText = parseTextList(dnsTxtDiscovery?.names).join('\n');
      values.dnsTxtDohEndpointsText = parseTextList(
        dnsTxtDiscovery?.dohEndpoints
      ).join('\n');
      values.dnsTxtMaxAgeSec = Number(dnsTxtDiscovery?.maxAgeSec || 900);
      values.dnsTxtExpectedKid = String(dnsTxtDiscovery?.expectedKid || '');
      values.connectionRotationEnabled = connectionRotation?.enabled === true;
      values.connectionRotationMinMinutes = Number(
        connectionRotation?.minMinutes || connectionRotation?.min_minutes || 15
      );
      values.connectionRotationMaxMinutes = Number(
        connectionRotation?.maxMinutes || connectionRotation?.max_minutes || 30
      );
      // Sprint 19 Wave 6 (W6-A) — DAITA per-node authoring. Mirrors the
      // five fields server-side `normalizeDaita` accepts. Absent block →
      // form shows disabled defaults; we don't synthesize a row, that's
      // the operator's explicit toggle action.
      const daita = (
        sk6Config.daita && typeof sk6Config.daita === 'object'
          ? sk6Config.daita
          : undefined
      ) as Record<string, unknown> | undefined;
      values.daitaEnabled = daita?.enabled === true;
      values.daitaMachineSpec =
        typeof daita?.machineSpec === 'string' ? daita.machineSpec : '';
      values.daitaReplyMachineSpec =
        typeof daita?.replyMachineSpec === 'string'
          ? daita.replyMachineSpec
          : '';
      values.daitaMaxPaddingFrac = clampFrac(
        daita?.maxPaddingFrac,
        DAITA_DEFAULT_MAX_PADDING_FRAC
      );
      values.daitaMaxBlockingFrac = clampFrac(
        daita?.maxBlockingFrac,
        DAITA_DEFAULT_MAX_BLOCKING_FRAC
      );
    } catch {
      values.protocols = [];
    }
  }

  if (nodeType === 'vless') {
    try {
      const config = parseConfigObject(node.config);
      values.vlessUuid = String(config.uuid || '');
      values.vlessWsPath = String(config.wsPath || '/');
      values.vlessWsHost = String(config.wsHost || '');
      values.vlessSni = String(config.sni || config.wsHost || '');
      values.expectedExitIpsText = parseTextList(
        config.expectedExitIps ?? config.egressIps
      ).join('\n');
      values.urltestUrl = String(config.urltestUrl || '');
      values.backupDomainsText = parseBackupDomains(
        node.backupDomains ?? config.backupDomains
      ).join('\n');
    } catch {
      values.config = node.config || '';
    }
  }

  return values;
}

export function buildNodePayload(
  values: NodeFormValues,
  existingConfig?: string
): Record<string, unknown> {
  const nodeType = values.nodeType || 'sk6';
  const backupDomains = parseBackupDomains(values.backupDomainsText);
  const payload: Record<string, unknown> = {
    name: values.name,
    displayName: values.displayName?.trim() || undefined,
    ip: values.ip,
    port: values.port,
    region: values.region,
    nodeType,
    nodeCategory: values.nodeCategory || 'primary',
    // 2026-07-02 — 營運標記：一律送出（清空時明確送 [] 讓後端存 null；
    // 送 undefined 會被序列化省略 → 後端收不到 → silent drop，同下方
    // 伺服器部署三欄的教訓）。trim + 去空由前端先做一層，後端
    // normalizeTagsForWrite 再雙保險。
    tags: (values.tags ?? []).map(tag => tag.trim()).filter(Boolean),
    isDecoy: values.isDecoy === true,
    allowedBrands: values.allowedBrands || [],
    // 2026-06-11 fail-closed：明確 opt-in 全品牌共享（後端只在 CREATE 消費）。
    shareAllBrands: values.shareAllBrands === true,
    minTier: values.minTier ?? 0,
    isEnabled: values.isEnabled !== false,
    backupDomains,
    // 監控欄位：enabled/port 一律送（boolean / number）。
    monitoringEnabled: values.monitoringEnabled === true,
    monitoringPort: values.monitoringPort ?? 9090,
    // 伺服器部署參數：表單有獨立輸入框，必須送出否則 operator 改了卻 silent
    // 不更新（DTO + 後端 allowedKeys 早已就緒接收，缺的就是這條 payload 鏈）。
    // 空字串 → 送 null（後端 DTO `.allow(null,'')`，清回 legacy AWS 預設）；
    // 非空 → trim 後送（pattern 驗證留給後端 DTO）。
    sshCredentialId: emptyToNull(values.sshCredentialId),
    deployPath: emptyToNull(values.deployPath),
    serviceName: emptyToNull(values.serviceName),
  };

  if (nodeType === 'vless') {
    let config: Record<string, unknown> = {};
    try {
      config = parseConfigObject(existingConfig);
    } catch {
      config = {};
    }
    config.uuid = values.vlessUuid;
    config.wsPath = values.vlessWsPath || '/';
    config.wsHost = values.vlessWsHost;
    config.sni = values.vlessSni || values.vlessWsHost;
    applyExitValidationConfig(config, values);
    if (backupDomains.length > 0) {
      config.backupDomains = backupDomains;
    } else {
      delete config.backupDomains;
    }
    payload.config = JSON.stringify(config);
  } else {
    // SK6：合併 textarea JSON + protocols 陣列（UI 獨立欄位）。
    // protocols 以 UI 為準：有 row 就寫入，空陣列則從 config 刪除。
    const config = parseConfigObject(values.config);
    const protocols = values.protocols ?? [];
    if (protocols.length > 0) {
      config.protocols = protocols;
    } else {
      delete config.protocols;
    }
    applyStructuredSk6Config(config, values);
    applyExitValidationConfig(config, values);
    const validatedConfig = validateSk6ConfigForSubmit(config);
    payload.config =
      Object.keys(validatedConfig).length > 0
        ? JSON.stringify(validatedConfig)
        : '';
  }

  return payload;
}

function validateSk6ConfigForSubmit(
  config: Record<string, unknown>
): Record<string, unknown> {
  const parsed = NodeSk6ConfigSchema.safeParse(config);
  if (parsed.success) return parsed.data;

  const issue = parsed.error.issues[0];
  const path = issue?.path.length ? `${issue.path.join('.')}: ` : '';
  throw new Error(`${path}${issue?.message || 'SK6 config 驗證失敗'}`);
}

function applyStructuredSk6Config(
  config: Record<string, unknown>,
  values: NodeFormValues
): void {
  setOptionalBoolean(config, 'relayOnly', values.relayOnly);
  setOptionalBoolean(config, 'originHidden', values.originHidden);
  if (values.relayOnly || values.originHidden) {
    config.allowDirect = false;
  } else if (config.allowDirect === false) {
    delete config.allowDirect;
  }

  const cdnDomain = values.cdnDomain?.trim();
  if (values.cdnRelayEnabled || cdnDomain) {
    const cdnPort = values.cdnPort ?? 443;
    const wsPort = values.sk6WsPort ?? 80;
    const wsPath = normalizePath(values.sk6WsPath || '/');
    config.cdnRelay = {
      enabled: true,
      domain: cdnDomain,
      port: cdnPort,
      wsPort,
      wsPath,
    };
    config.cdnDomain = cdnDomain;
    config.cdnPort = cdnPort;
    config.sk6WsPort = wsPort;
    config.sk6WsPath = wsPath;
  } else {
    delete config.cdnRelay;
    delete config.cdnDomain;
    delete config.cdnPort;
    delete config.sk6WsPort;
    delete config.sk6WsPath;
  }

  const anytlsDomain = values.anytlsDomain?.trim();
  const anytlsPassword = values.anytlsPassword?.trim();
  if (values.anytlsEnabled || anytlsDomain || anytlsPassword) {
    config.anytls = {
      enabled: true,
      domain: anytlsDomain,
      port: values.anytlsPort ?? 443,
      password: anytlsPassword,
      ...(values.anytlsSni?.trim() ? { sni: values.anytlsSni.trim() } : {}),
    };
  } else {
    delete config.anytls;
  }

  if (values.fallbackPriority?.length) {
    config.fallbackPriority = values.fallbackPriority;
  } else {
    delete config.fallbackPriority;
  }

  // AB-D6 — only emit fingerprintMap when at least one protocol has a non-empty
  // override; an empty object means "use compiled-in client defaults" and adds
  // nothing on the wire.
  const fingerprintMap = compactFingerprintMap(values.fingerprintMap);
  if (fingerprintMap) {
    config.fingerprintMap = fingerprintMap;
  } else {
    delete config.fingerprintMap;
  }

  if (values.connectionRotationEnabled) {
    config.connectionRotation = {
      enabled: true,
      minMinutes: values.connectionRotationMinMinutes ?? 15,
      maxMinutes: values.connectionRotationMaxMinutes ?? 30,
    };
    delete config.rotation;
  } else {
    delete config.connectionRotation;
    delete config.rotation;
  }

  // Sprint 19 Wave 6 (W6-A) — DAITA per-node authoring. Emit the block
  // when the operator has touched any DAITA field (enabled OR machine
  // spec OR reply spec) — that signals deliberate authoring. Absent
  // emission keeps zero behaviour change for Wave 1-5 nodes.
  const daitaMachineSpec = values.daitaMachineSpec?.trim() ?? '';
  const daitaReplyMachineSpec = values.daitaReplyMachineSpec?.trim() ?? '';
  const daitaTouched =
    values.daitaEnabled === true ||
    daitaMachineSpec.length > 0 ||
    daitaReplyMachineSpec.length > 0;
  if (daitaTouched) {
    config.daita = {
      enabled: values.daitaEnabled === true,
      machineSpec: daitaMachineSpec,
      replyMachineSpec: daitaReplyMachineSpec,
      maxPaddingFrac: clampFrac(
        values.daitaMaxPaddingFrac,
        DAITA_DEFAULT_MAX_PADDING_FRAC
      ),
      maxBlockingFrac: clampFrac(
        values.daitaMaxBlockingFrac,
        DAITA_DEFAULT_MAX_BLOCKING_FRAC
      ),
    };
  } else {
    delete config.daita;
  }

  const txtNames = parseTextList(values.dnsTxtNamesText);
  const dohEndpoints = parseTextList(values.dnsTxtDohEndpointsText);
  if (values.dnsTxtDiscoveryEnabled || txtNames.length || dohEndpoints.length) {
    config.dnsTxtDiscovery = {
      enabled: true,
      names: txtNames,
      dohEndpoints,
      maxAgeSec: values.dnsTxtMaxAgeSec ?? 900,
      ...(values.dnsTxtExpectedKid?.trim()
        ? { expectedKid: values.dnsTxtExpectedKid.trim() }
        : {}),
    };
  } else {
    delete config.dnsTxtDiscovery;
  }
}

function applyExitValidationConfig(
  config: Record<string, unknown>,
  values: NodeFormValues
): void {
  const expectedExitIps = parseTextList(values.expectedExitIpsText);
  if (expectedExitIps.length > 0) {
    config.expectedExitIps = expectedExitIps;
    config.egressIps = expectedExitIps;
  } else {
    delete config.expectedExitIps;
    delete config.egressIps;
  }

  const urltestUrl = values.urltestUrl?.trim();
  if (urltestUrl) {
    config.urltestUrl = urltestUrl;
  } else {
    delete config.urltestUrl;
  }
}

function setOptionalBoolean(
  config: Record<string, unknown>,
  key: 'relayOnly' | 'originHidden',
  value: boolean | undefined
): void {
  if (value) {
    config[key] = true;
  } else {
    delete config[key];
  }
}

function normalizePath(value: string): string {
  return value.startsWith('/') ? value : `/${value}`;
}

/**
 * 把可選字串欄位（伺服器部署參數）normalize 成 payload 值：
 *   - 留空 / 全空白 → `null`（後端 DTO `.allow(null, '')` 接受，清回 legacy 預設）
 *   - 有值          → trim 後的字串
 * 一律回傳明確值（null 或 string），確保欄位「有送」——這是修掉 silent drop
 * 的關鍵：缺值送 undefined 會被序列化省略，後端就收不到、改了等於沒改。
 */
function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * AB-D6 — parses a stored fingerprintMap JSON into the form-state shape.
 * Tolerates legacy / partial entries (only known keys + non-empty strings
 * are kept). Returns undefined when nothing valid remains so the form can
 * present empty dropdowns (= "client default").
 */
function parseFingerprintMap(raw: unknown): FingerprintMap | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: FingerprintMap = {};
  const allowedKeys = new Set<string>(FINGERPRINT_PROTOCOL_KEYS);
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!allowedKeys.has(key)) continue;
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    out[key as FingerprintProtocolKey] = trimmed as FingerprintMap[FingerprintProtocolKey];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * AB-D6 — strips empty-string ("client default") entries before persistence
 * so the wire never carries entries the client wouldn't act on. The server
 * also re-validates and drops unknown values; this is the BO-side belt.
 */
function compactFingerprintMap(
  value: FingerprintMap | undefined
): FingerprintMap | undefined {
  if (!value) return undefined;
  const out: FingerprintMap = {};
  for (const key of FINGERPRINT_PROTOCOL_KEYS) {
    const v = value[key];
    if (typeof v === 'string' && v.trim()) {
      out[key] = v.trim() as FingerprintMap[FingerprintProtocolKey];
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
